<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormatStore } from '@/stores/formatStore'

const { t } = useI18n()
const formatStore = useFormatStore()

// Props for reusable data
const props = defineProps<{
  yacht: any
  availability: any
  priceDetails: any
  selectedDatesText: string
  selections: { persons: number }
}>()

// Scroll & resize logic
const bottom = ref(500)
const windowCurrSize = ref(window.innerWidth)
const scroll = ref(0)

function onScroll() {
  scroll.value = window.scrollY;
  const element = document.querySelector('.boat-data-header-bottom')
  if (element) {
    const rect = element.getBoundingClientRect()
    bottom.value = parseInt(rect.bottom)
  }
}

function windowSize() {
  windowCurrSize.value = window.innerWidth
}

const threshold = computed(() => {
  if (windowCurrSize.value < 480) return 40
  if (windowCurrSize.value < 768) return 50
  return 75
})

const shouldShowHeader = computed(() => props.yacht && bottom.value < threshold.value)

onMounted(() => {
  window.addEventListener('scroll', onScroll, true)
  window.addEventListener('resize', windowSize)
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll, true)
  window.removeEventListener('resize', windowSize)
})
</script>

<template>
  <Transition name="scroll">
    <div
        v-if="shouldShowHeader"
        v-show="availability?.available"
        class="boat-data-header py-2 shadow top-18 bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue fixed w-full h-fit z-10"
    >
      <div class="container flex flex-wrap sm:flex-nowrap justify-between items-center py-2 sm:py-4">
        <!-- Left Section -->
        <div class="flex flex-col flex-wrap sm:flex-nowrap sm:gap-5 justify-between">
          <div class="flex flex-col sm:flex-row sm:items-center sm:gap-5 w-full">
            <!-- Date -->
            <div class="flex items-center gap-2">
              <img class="h-4 img-dp" src="@/assets/icons/svg/Calendar_16x16.svg" alt="calendar" />
              <span class="date-content responsive-body-small">{{ selectedDatesText }}</span>
            </div>

            <!-- Passengers -->
            <div class="flex items-center gap-2">
              <img class="h-4 img-dp" src="@/assets/icons/svg/Guest_16x16.svg" alt="guests" />
              <span class="responsive-body-small">
                {{ selections.persons }} {{ t('passengers') }} in {{ yacht.cabins }} {{ t('cabins') }}
              </span>
            </div>
          </div>

          <!-- Price on Small Screens -->
          <div class="block sm:hidden price responsive-body-medium mt-1 sm:mt-2">
            <span class="responsive-title-5 font-semibold">
              {{ formatStore.formatCurrency(priceDetails.total) }}
            </span>
          </div>
        </div>

        <!-- Right Section -->
        <div class="flex flex-col custom-flex-row items-center gap-0 sm:gap-5">
          <!-- Price on Larger Screens -->
          <div class="hidden sm:block responsive-body-medium text-right mt-2 sm:mt-0">
            <span class="responsive-title-5 font-semibold">
              {{ formatStore.formatCurrency(priceDetails.total) }}
            </span>
          </div>

          <!-- Booking Control Slot -->
          <slot name="booking-control" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
@media (min-width: 550px) {
  .custom-flex-row {
    flex-direction: row !important;
    gap: 1.25rem;
  }
}


@media (max-width: 499px) {
  .custom-flex-row {
    flex-direction: row !important;
    gap: 0.5rem;
  }

}
</style>
